import * as ko from "knockout";

import { addBindingHandler } from "./util";


/**
 * Adaptation of knockout's default "hasFocus" binding to work with Pli picklist fields in addition to normal input fields.
 *
 * Tweaked version of https://github.com/knockout/knockout/blob/master/src/binding/defaultBindings/hasfocus.js
 */
export interface PliHasFocusBinding {
    init: (element: HTMLElement, valueAccessor: () => ko.MaybeSubscribable<any>, allBindingsAccessor: ko.AllBindingsAccessor) => void;
    update: (element: HTMLElement, valueAccessor: () => ko.MaybeSubscribable<any>) => void;
}


const hasFocusUpdatingProperty = "__kopli_hasFocusUpdating";
const hasFocusLastValue = "__kopli_hasFocusLastValue";
const hasFocusRegistrator = "__kopli_hasFocusRegistrator";
const hasFocusUpdater = "__kopli_hasFocusUpdater";


export type FocusRegistrator = (handleElementFocusIn: () => void, handleElementFocusOut: () => void, element: HTMLElement) => void;
export type FocusUpdater = (focusToSet: boolean) => void;


/** Set the function that should be used to register for focus in and out events. */
export function setFieldFocusRegistrator(element: HTMLElement, registrator: FocusRegistrator): void {
    if (!element) { throw new Error("pliHasFocus: Target element is required."); }
    if (typeof registrator !== "function") { throw new Error("pliHasFocus: Registrator must be a function."); }

    (<any>element)[hasFocusRegistrator] = registrator;
}


/** Set the function that should be used to focus or blur an element. */
export function setFieldFocusUpdater(element: HTMLElement, updater: FocusUpdater): void {
    if (!element) { throw new Error("pliHasFocus: Target element is required."); }
    if (typeof updater !== "function") { throw new Error("pliHasFocus: Updater must be a function."); }

    (<any>element)[hasFocusUpdater] = updater;
}



const pliHasFocusBinding: PliHasFocusBinding = {

    init: (element, valueAccessor, allBindings) => {
        const focusRegistrator: FocusRegistrator = (<any>element)[hasFocusRegistrator];
        if (typeof focusRegistrator === "function") {
            if (typeof (<any>element)[hasFocusUpdater] !== "function") {
                throw new Error("pliHasFocus: If a focus registrator is assigned to an element, a focus updater must also be assigned.");
            }

            const handleElementFocusChange = function (isFocused: boolean): void {
                // Where possible, ignore which event was raised and determine focus state using activeElement,
                // as this avoids phantom focus/blur events raised when changing tabs in modern browsers.
                // However, not all KO-targeted browsers (Firefox 2) support activeElement. For those browsers,
                // prevent a loss of focus when changing tabs/windows by setting a flag that prevents hasfocus
                // from calling 'blur()' on the element when it loses focus.
                // Discussion at https://github.com/SteveSanderson/knockout/pull/352
                (<any>element)[hasFocusUpdatingProperty] = true;
                const ownerDoc = element.ownerDocument;
                if (ownerDoc.hasOwnProperty("activeElement")) {
                    let activeElement: any;
                    try {
                        activeElement = ownerDoc.activeElement;
                    } catch (e) {
                        // IE9 throws if you access activeElement during page load (see issue #703)
                        activeElement = ownerDoc.body;
                    }
                    isFocused = (activeElement === element);
                }
                const modelValue = valueAccessor();
                (<any> ko.expressionRewriting).writeValueToProperty(modelValue, allBindings, "pliHasFocus", isFocused, true);

                // cache the latest value, so we can avoid unnecessarily calling focus/blur in the update function
                (<any>element)[hasFocusLastValue] = isFocused;
                (<any>element)[hasFocusUpdatingProperty] = false;
            };
            const handleElementFocusIn = handleElementFocusChange.bind(null, true);
            const handleElementFocusOut = handleElementFocusChange.bind(null, false);

            focusRegistrator(handleElementFocusIn, handleElementFocusOut, element);
        } else {
            ko.bindingHandlers.hasfocus.init(element, valueAccessor, allBindings);
        }
    },

    update: (element, valueAccessor) => {
        const updater: FocusUpdater = (<any>element)[hasFocusUpdater];
        if (typeof updater === "function") {
            const value = !!ko.unwrap(valueAccessor());
            if (!(<any>element)[hasFocusUpdatingProperty] && (<any>element)[hasFocusLastValue] !== value) {
                updater(value);
            }
        } else {
            ko.bindingHandlers.hasfocus.update(element, valueAccessor);
        }
    }

};


/**
 * Activates the 'pliHasFocus' binding.
 */
export function activatePliHasFocusBinding(): PliHasFocusBinding {
    const handler = addBindingHandler("pliHasFocus", pliHasFocusBinding);

    if (!(<any> ko.expressionRewriting).twoWayBindings.pliHasFocus) {
        (<any> ko.expressionRewriting).twoWayBindings.pliHasFocus = true;
    }

    return handler;
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/knockout/pliHasFocus.ts